/**
 * Fluencia Mobile App
 * Main entry point - initializes auth and renders navigation
 *
 * Note: Sentry is initialized and App is wrapped with Sentry.wrap() in index.ts
 * BEFORE this file is imported to ensure initialization errors are captured.
 */

import './src/i18n';

import React, { useEffect, useState } from "react";
import * as SplashScreen from 'expo-splash-screen';
import * as ScreenOrientation from 'expo-screen-orientation';
import { StatusBar } from "expo-status-bar";
import { NavigationBar } from "expo-navigation-bar";
import { Alert, LogBox, Platform, View } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { KeyboardProvider } from "react-native-keyboard-controller";
import * as Updates from "expo-updates";
import i18n from './src/i18n';
import { initI18n } from './src/i18n';
import { AppNavigator } from "./src/navigation/AppNavigator";
import { useColorMode } from "./src/contexts/ColorModeContext";
import { initializeAuth, createSupabaseAdapter } from "./src/auth";
import { freshnessManager } from "./src/services/freshnessManager";
import { DOMAIN_REFRESH_MAP } from "./src/generated/sync/sync-config";
import { apiClient } from "./src/services/apiClient";
import { useUserPreferencesStore } from "./src/store/userPreferencesStore";
import { useSubscriptionStore } from "./src/store/subscriptionStore";
import { GluestackUIProvider } from "./src/components/providers/GluestackUIProvider";
import * as Sentry from "@sentry/react-native";
import { createLogger } from "./src/utils/logger";
import { API_URL, E2E_MODE, getDevHost, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "./src/config/constants";
import { useCourseStore } from "./src/store/courseStore";
import { useMessageStore } from "./src/store/messageStore";
import { InAppMessaging } from "./src/components/ui/InAppMessaging";

const log = createLogger("App");

// Keep splash screen visible until app is fully ready (auth + preferences loaded)
SplashScreen.preventAutoHideAsync().catch(() => {});

// E2E mode: silence LogBox notification toasts at boot so they
// can't overlay interactive elements like the consent-modal Accept
// button (CONV-009 was failing for this exact reason — a leftover
// `console.error('Server rejected message')` toast from the prior
// branch covered chat-button-accept on the next branch's tap).
//
// Per React Native LogBox source: ignoreAllLogs(true) ONLY disables
// the notification overlay — uncaught exceptions still open the
// full-screen RedBox. The framework's earlier hook in `devReset.ts`
// (on `dev/reset-nav`) silences late, after the first toast may
// already be on screen; silencing at module top-level guarantees no
// toast ever renders. Production tree-shakes this — `__DEV__` is
// always false outside dev builds, and `E2E_MODE` is always false
// outside `E2E_MODE=true` runs.
if (__DEV__ && E2E_MODE) {
  LogBox.ignoreAllLogs(true);
}

if (__DEV__) {
  const sentryClient = Sentry.getClient();
  const sentryDsn = sentryClient?.getDsn();
  log.info(`API URL: ${API_URL}`);
  log.info(`E2E_MODE: ${E2E_MODE}`);
  log.info(`Sentry DSN: ${sentryDsn ? `${sentryDsn.host}/${sentryDsn.projectId}` : '(not initialized)'}`);
  log.info(`Spotlight URL: http://${getDevHost()}:8970/stream`);

  // Stream AsyncStorage mutations to dev-dashboard (Live tab on /sync-config)
  require('./src/dev/storage-observer').initStorageObserver();
}

// Suppress dev menu for E2E mode.
// DevMenuPreferences native module only exists on iOS, so we guard with try-catch.
// isOnboardingFinished must be written directly to UserDefaults (iOS) since
// the native setPreferencesAsync doesn't handle that key.
//
// showFloatingActionButton: hides the floating "tools button" overlay that
// expo-dev-menu 55.0.3+ renders on top of the app UI in dev-client builds.
// We saw it occluding the chat-history button during CONV-002's tap intent
// (the FAB got tapped instead, knocking the flow off + surfacing the dev
// menu mid-run). The native module writes to UserDefaults (iOS) /
// SharedPreferences (Android) and calls DevMenuManager.updateFABVisibility()
// so the FAB hides on the next render — no prebuild required. Same store
// the dev menu's UI toggle writes to, so manually toggling "Show tools
// button" from inside the running app produces the same effect.
if (__DEV__ && Platform.OS !== 'web' && E2E_MODE) {
  try {
    const { requireNativeModule } = require('expo-modules-core');
    const DevMenuPreferences = requireNativeModule('DevMenuPreferences');
    DevMenuPreferences.setPreferencesAsync({
      motionGestureEnabled: false,
      touchGestureEnabled: false,
      showsAtLaunch: false,
      showFloatingActionButton: false,
    });
  } catch {}
  if (Platform.OS === 'ios') {
    const { Settings } = require('react-native');
    Settings.set({ EXDevMenuIsOnboardingFinished: true });
  }
}

/**
 * Check for OTA updates in the background
 * Shows a prompt if an update is available
 */
async function checkForUpdates(): Promise<void> {
  // Skip on web (not supported) and in dev mode
  if (Platform.OS === "web" || __DEV__) {
    return;
  }

  try {
    const update = await Updates.checkForUpdateAsync();
    if (update.isAvailable) {
      log.info("OTA update available, downloading...");
      await Updates.fetchUpdateAsync();
      Alert.alert(
        i18n.t('updates.availableTitle'),
        i18n.t('updates.availableMessage'),
        [
          { text: i18n.t('updates.later'), style: "cancel" },
          { text: i18n.t('updates.restart'), onPress: () => Updates.reloadAsync() },
        ]
      );
    }
  } catch (e) {
    // Silently fail - OTA check is non-critical
    log.debug("OTA update check failed (expected in dev)", { error: String(e) });
  }
}

/** Ties the system bars (status + Android navigation) to Fluencia's theme (not the OS setting). */
function ThemedSystemBars() {
  const { isDark } = useColorMode();
  const style = isDark ? 'light' : 'dark';
  return (
    <>
      <StatusBar style={style} />
      {/* No-op outside Android — SDK 56 component, merges with StatusBar conventions */}
      <NavigationBar style={style} />
    </>
  );
}

function App() {
  // Gate the first paint on i18n init: the synchronous i18next init defaults
  // to en-US, and the real UI language (stored choice / OS detection) lands
  // async. Rendering before it resolves paints English and visibly flips.
  // The native splash stays up (preventAutoHideAsync above) while we wait.
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    // Lock phones to portrait; tablets get all orientations
    const isPad = Platform.OS === 'ios' && (Platform as any).isPad;
    if (Platform.OS !== 'web' && !isPad) {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {});
    }

    // Initialize i18n (detect OS/stored language). Never blocks the app on
    // failure — worst case the UI stays on the en-US default.
    initI18n()
      .catch((e) => log.warn("i18n init failed — continuing with en-US default", { error: String(e) }))
      .finally(() => setI18nReady(true));

    // Dev-only: listen for the clear-state deep link from the dev-dashboard.
    // When it arrives, AsyncStorage is wiped and the JS bundle is reloaded.
    let unregisterDevReset: (() => void) | undefined;
    if (__DEV__) {
      import("./src/dev/devReset").then(({ registerDevResetListener }) => {
        unregisterDevReset = registerDevResetListener();
      }).catch(() => { /* non-fatal */ });
    }

    async function startup() {
      // Load persisted freshness state (before auth, so the version
      // interceptor has the correct baseline on the first API call)
      // Register domains that have a generated refresh function + store hydration
      // Register domain refresh functions with freshnessManager.
      // userProfile uses the store's fetchProfile (handles 404 recovery + reconciliation).
      // courses uses the generated refresh + store hydration (its cache is the
      // raw response shape, so the generated save is valid).
      freshnessManager.register('userProfile', async () => {
        await useUserPreferencesStore.getState().fetchProfile();
      });
      freshnessManager.register('courses', async () => {
        const refresh = DOMAIN_REFRESH_MAP.courses;
        if (refresh) await refresh(apiClient);
        await useCourseStore.getState().loadFromCache();
      });
      // messages must NOT use the generated DOMAIN_REFRESH_MAP.messages:
      // its cache schema extends the response with `dismissed`, and the
      // generated refresh saves the raw GET response — every TTL refresh
      // would overwrite the user's persisted dismissals (the MSG-006
      // protection). fetchActiveMessages fetches, prunes + preserves the
      // dismissed set, persists both halves, and hydrates the in-memory
      // store in one step.
      freshnessManager.register('messages', async () => {
        const ok = await useMessageStore.getState().fetchActiveMessages();
        // Throw on failure so executeRefresh doesn't stamp lastSyncedAt —
        // the domain stays stale and retries on the next natural trigger.
        if (!ok) throw new Error('messages refresh failed');
      });

      // Lessons has no cache — custom refresh logic
      freshnessManager.register('lessons', async () => {
        const { useUserPreferencesStore: prefs } = await import("./src/store/userPreferencesStore");
        const { useLessonStore: lessons } = await import("./src/store/lessonStore");
        const { api: apiService } = await import("./src/services/api");
        const enrolled = prefs.getState().coursePreferences;
        if (enrolled.length === 0) return;
        const courses = enrolled.map((p) => ({
          courseId: p.courseId,
          lessonVersion: '1970-01-01T00:00:00.000Z', // TODO: track per-course lessonVersion in AsyncStorage
        }));
        const result = await apiService.checkLessonUpdates({ courses });
        if (!result.ok) return;
        const updated = (result.data as any).updatedCourses?.map((c: any) => c.courseId) ?? [];
        if (updated.length > 0) {
          lessons.getState().setLessonUpdatesAvailable(updated);
        }
      });
      // E2E-only checkpoint: park here until the test releases the
      // barrier via `dev.continue-boot`. Lets tests seed AsyncStorage
      // (cache, freshness state) BEFORE the disk reads below consult
      // it. No-op in production — see bootBarrier.ts for the full
      // design rationale and auto-release safeguard.
      if (__DEV__ && E2E_MODE) {
        const { waitForBootBarrier } = await import("./src/dev/bootBarrier");
        await waitForBootBarrier();
      }

      await freshnessManager.loadState().catch(() => {
        // AsyncStorage read failed — continue with version 0.
        // The first API response will establish the baseline.
      });

      // Hydrate the messages in-memory store from its on-disk cache
      // unconditionally at boot. This is what other domains' UIs get
      // for free via their store's cache-then-fetch entry points
      // (`courseStore.load`, `userPreferencesStore.fetchProfile`),
      // both of which are called from AppNavigator on auth change.
      // `useMessageStore` has no equivalent — its `loadFromCache` runs
      // ONLY inside the freshness-registered callback, which fires
      // only when the messages domain is stale.
      //
      // Without this call, a cold start within the messages TTL (1h)
      // leaves the in-memory store empty even when the cache on disk
      // has data — UI shows nothing until the next refresh trigger
      // (foreground event, version-drift signal, manual refresh) marks
      // the domain stale. MSG-005's "Cache hit" branch exercises this
      // path and would fail without this hydration step.
      //
      // Failure is non-fatal: an unreadable cache leaves the store
      // empty, which is the same state the user gets on a real first
      // install. The next freshness refresh will populate it.
      await useMessageStore.getState().loadFromCache().catch(() => {});

      // Initialize auth (adapter + state machine + session restore)
      const provider = createSupabaseAdapter(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);
      initializeAuth(provider);

      // Start foreground monitoring (after auth — domain refreshes need auth)
      freshnessManager.startListening();
    }
    startup();

    // Check for OTA updates in background
    checkForUpdates();

    if (__DEV__ && Platform.OS !== 'web') {
      if (E2E_MODE) {
        // Close dev menu after native auto-launch (fires on RCTContentDidAppear, before useEffect)
        const DevMenu = require('expo-dev-menu');
        DevMenu.closeMenu();
      } else {
        // Restore dev menu gestures + tools button when not in E2E mode.
        // Without this, a previous E2E session's `false` preferences
        // would persist in UserDefaults / SharedPreferences and leave
        // the dev affordances disabled for the next normal-dev launch.
        try {
          const { requireNativeModule } = require('expo-modules-core');
          const DevMenuPreferences = requireNativeModule('DevMenuPreferences');
          DevMenuPreferences.setPreferencesAsync({
            motionGestureEnabled: true,
            touchGestureEnabled: true,
            showFloatingActionButton: true,
          });
        } catch {}
      }
    }

    return () => {
      unregisterDevReset?.();
    };
  }, []);

  if (!i18nReady) {
    return null;
  }

  return (
    <View testID="app-ready" style={{ flex: 1 }}>
      <GluestackUIProvider>
        <SafeAreaProvider>
          <KeyboardProvider>
            <AppNavigator />
            <InAppMessaging />
            <ThemedSystemBars />
          </KeyboardProvider>
        </SafeAreaProvider>
      </GluestackUIProvider>
    </View>
  );
}

export default App;
